/**
 * BIGWA - Outline Store v3.0
 * 
 * Store Zustand para gerenciar o estado do outline.
 * Controla seções, alocações de blocos, janela flutuante e persistência.
 * 
 * v2.0: Adicionado isPinned para fixar posição da janela
 * v3.0: Adicionado items[] para "Enviar ao Outline" e selectedContents[] para seleção
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { safeSetItem } from '../utils/safeLocalStorage';
import {
  OutlineSection,
  OutlineItemPlacement,
  OutlineItem,
  OutlineNote,
  ProjectOutline,
  OutlinePanelState,
  OutlineViewPreferences,
  MoveDirection,
  SectionWithBlocks,
  SelectedBlockContent,
  SelectableContentType,
  DEFAULT_PANEL_STATE,
  DEFAULT_VIEW_PREFERENCES
} from '../types/outline';
import { logger } from '../utils/logger';

// ============================================================
// HELPER: Gerar IDs únicos
// ============================================================

const generateId = (prefix: string): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================================
// LOCAL STORAGE para posição (isPinned)
// ============================================================

const OUTLINE_PANEL_UI_KEY = 'bigwa-outline-panel-ui';

interface SavedUIState {
  position: { x: number; y: number };
  size: { width: number; height: number };
  isPinned: boolean;
}

function loadUIState(): SavedUIState | null {
  try {
    const saved = localStorage.getItem(OUTLINE_PANEL_UI_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    logger.error('OutlineStore', 'Erro ao carregar UI:', e);
  }
  return null;
}

function saveUIState(state: SavedUIState) {
  try {
    safeSetItem(OUTLINE_PANEL_UI_KEY, JSON.stringify(state));
  } catch (e) {
    logger.error('OutlineStore', 'Erro ao salvar UI:', e);
  }
}

// ============================================================
// INTERFACE DO STORE
// ============================================================

interface OutlineStore {
  // === ESTADO ===
  sections: OutlineSection[];
  placements: OutlineItemPlacement[];
  items: OutlineItem[];                    // 🆕 v3.0: Itens enviados de Conexões
  notes: OutlineNote[];
  lastModified: string;
  panelState: OutlinePanelState;
  viewPreferences: OutlineViewPreferences;
  isPinned: boolean;
  
  // 🆕 v3.0: Seleção em Conexões
  selectedContents: SelectedBlockContent[];
  
  // Estado de loading/saving
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean; // true se há mudanças não salvas
  
  // === SEÇÕES ===
  addSection: (title: string, parentId?: string | null) => string;
  updateSection: (id: string, updates: Partial<Pick<OutlineSection, 'title' | 'isCollapsed'>>) => void;
  deleteSection: (id: string) => void;
  moveSection: (id: string, direction: MoveDirection) => void;
  indentSection: (id: string) => void;
  outdentSection: (id: string) => void;
  toggleSectionCollapse: (id: string) => void;
  
  // === BLOCOS (ALOCAÇÃO LEGADO) ===
  allocateBlock: (blockId: string, sectionId: string) => void;
  deallocateBlock: (blockId: string) => void;
  moveBlock: (blockId: string, toSectionId: string, toIndex?: number) => void;
  moveBlockInSection: (blockId: string, direction: MoveDirection) => void;
  isBlockAllocated: (blockId: string) => boolean;
  getBlockSection: (blockId: string) => string | null;
  
  // === 🆕 v3.0: ITEMS (Enviados de Conexões) ===
  addItem: (item: Omit<OutlineItem, 'id' | 'order' | 'createdAt' | 'updatedAt'>) => string;
  addItems: (items: Omit<OutlineItem, 'id' | 'order' | 'createdAt' | 'updatedAt'>[]) => string[];
  removeItem: (itemId: string) => void;
  updateItem: (itemId: string, updates: Partial<OutlineItem>) => void;
  moveItemToSection: (itemId: string, sectionId: string | null) => void;
  moveItemPosition: (itemId: string, direction: MoveDirection) => void;
  indentItem: (itemId: string) => void;
  outdentItem: (itemId: string) => void;
  getInboxItems: () => OutlineItem[];
  getSectionItems: (sectionId: string) => OutlineItem[];
  getItemCount: () => { inbox: number; total: number };
  
  // === 🆕 v3.0: SELEÇÃO EM CONEXÕES ===
  toggleContentSelection: (content: SelectedBlockContent) => void;
  clearSelection: () => void;
  isContentSelected: (blockId: string, type: SelectableContentType) => boolean;
  sendSelectionToOutline: () => void;
  getSelectionCount: () => number;
  
  // === NOTAS LIVRES ===
  addNote: (sectionId: string, text: string) => string;
  updateNote: (noteId: string, text: string) => void;
  deleteNote: (noteId: string) => void;
  
  // === JANELA FLUTUANTE ===
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  togglePin: () => void;
  updatePanelPosition: (x: number, y: number) => void;
  updatePanelSize: (width: number, height: number) => void;
  setFocusedSection: (sectionId: string | null) => void;
  setFocusedBlock: (blockId: string | null) => void;
  
  // === PREFERÊNCIAS ===
  updateViewPreferences: (prefs: Partial<OutlineViewPreferences>) => void;
  
  // === PERSISTÊNCIA ===
  loadFromProject: (outline: ProjectOutline | null) => void;
  getProjectOutline: () => ProjectOutline;
  markAsSaved: () => void;
  
  // === HELPERS ===
  getSectionHierarchy: () => SectionWithBlocks[];
  getSectionById: (id: string) => OutlineSection | undefined;
  getBlocksInSection: (sectionId: string) => OutlineItemPlacement[];
  getNotesInSection: (sectionId: string) => OutlineNote[];
  getTotalBlocksAllocated: () => number;
  getSectionChildren: (parentId: string | null) => OutlineSection[];
  getBlockPlacement: (blockId: string) => OutlineItemPlacement | null;
  
  // === ALIASES (compatibilidade com ConexoesInspector) ===
  removeBlockFromOutline: (blockId: string) => void;
  moveBlockPosition: (blockId: string, direction: MoveDirection) => void;
  moveBlockToSection: (blockId: string, toSectionId: string) => void;
  
  // === RESET ===
  reset: () => void;
}

// ============================================================
// STORE IMPLEMENTATION
// ============================================================

// Carregar UI salva
const savedUI = loadUIState();

export const useOutlineStore = create<OutlineStore>()(
  devtools(
    (set, get) => ({
      // === ESTADO INICIAL ===
      sections: [],
      placements: [],
      items: [],                 // 🆕 v3.0
      selectedContents: [],      // 🆕 v3.0
      notes: [],
      lastModified: new Date().toISOString(),
      panelState: savedUI 
        ? { ...DEFAULT_PANEL_STATE, position: savedUI.position, size: savedUI.size }
        : { ...DEFAULT_PANEL_STATE },
      viewPreferences: { ...DEFAULT_VIEW_PREFERENCES },
      isPinned: savedUI?.isPinned || false,
      isLoading: false,
      isSaving: false,
      isDirty: false,

      // ============================================================
      // SEÇÕES
      // ============================================================

      addSection: (title: string, parentId: string | null = null) => {
        const id = generateId('sec');
        const { sections } = get();
        
        // Calcular nível baseado no pai
        let level = 1;
        if (parentId) {
          const parent = sections.find(s => s.id === parentId);
          if (parent) level = parent.level + 1;
        }
        
        // Calcular ordem (último do mesmo nível/pai)
        const siblings = sections.filter(s => s.parentId === parentId);
        const order = siblings.length;
        
        const newSection: OutlineSection = {
          id,
          title,
          parentId,
          order,
          level,
          isCollapsed: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        set(state => ({
          sections: [...state.sections, newSection],
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
        
        return id;
      },

      updateSection: (id: string, updates: Partial<Pick<OutlineSection, 'title' | 'isCollapsed'>>) => {
        set(state => ({
          sections: state.sections.map(s =>
            s.id === id
              ? { ...s, ...updates, updatedAt: new Date().toISOString() }
              : s
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      deleteSection: (id: string) => {
        const { sections } = get();
        
        // Encontrar todos os IDs a deletar (seção + subseções)
        const idsToDelete = new Set<string>();
        const collectChildren = (parentId: string) => {
          idsToDelete.add(parentId);
          sections.filter(s => s.parentId === parentId).forEach(s => collectChildren(s.id));
        };
        collectChildren(id);
        
        set(state => ({
          sections: state.sections.filter(s => !idsToDelete.has(s.id)),
          placements: state.placements.filter(p => !idsToDelete.has(p.sectionId)),
          items: state.items.map(item => 
            idsToDelete.has(item.sectionId || '') 
              ? { ...item, sectionId: null } // Move para inbox ao invés de deletar
              : item
          ),
          notes: state.notes.filter(n => !idsToDelete.has(n.sectionId)),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      moveSection: (id: string, direction: MoveDirection) => {
        const { sections } = get();
        const section = sections.find(s => s.id === id);
        if (!section) return;
        
        // Encontrar irmãos (mesmo pai)
        const siblings = sections
          .filter(s => s.parentId === section.parentId)
          .sort((a, b) => a.order - b.order);
        
        const currentIndex = siblings.findIndex(s => s.id === id);
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        if (newIndex < 0 || newIndex >= siblings.length) return;
        
        // Trocar ordens
        const targetId = siblings[newIndex].id;
        
        set(state => ({
          sections: state.sections.map(s => {
            if (s.id === id) return { ...s, order: newIndex };
            if (s.id === targetId) return { ...s, order: currentIndex };
            return s;
          }),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      indentSection: (id: string) => {
        const { sections } = get();
        const section = sections.find(s => s.id === id);
        if (!section) return;
        
        // Encontrar irmão anterior (será o novo pai)
        const siblings = sections
          .filter(s => s.parentId === section.parentId)
          .sort((a, b) => a.order - b.order);
        
        const currentIndex = siblings.findIndex(s => s.id === id);
        if (currentIndex <= 0) return; // Precisa ter irmão anterior
        
        const newParentId = siblings[currentIndex - 1].id;
        
        // Calcular nova ordem (último filho do novo pai)
        const newSiblings = sections.filter(s => s.parentId === newParentId);
        const newOrder = newSiblings.length;
        
        set(state => ({
          sections: state.sections.map(s =>
            s.id === id
              ? { ...s, parentId: newParentId, level: s.level + 1, order: newOrder }
              : s
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      outdentSection: (id: string) => {
        const { sections } = get();
        const section = sections.find(s => s.id === id);
        if (!section || !section.parentId) return; // Não pode outdent da raiz
        
        const parent = sections.find(s => s.id === section.parentId);
        if (!parent) return;
        
        // Nova posição: depois do pai, no nível do pai
        const parentSiblings = sections
          .filter(s => s.parentId === parent.parentId)
          .sort((a, b) => a.order - b.order);
        
        const parentIndex = parentSiblings.findIndex(s => s.id === parent.id);
        const newOrder = parentIndex + 1;
        
        // Ajustar ordem dos irmãos do pai que vêm depois
        set(state => ({
          sections: state.sections.map(s => {
            if (s.id === id) {
              return { ...s, parentId: parent.parentId, level: parent.level, order: newOrder };
            }
            // Ajustar ordem dos que vêm depois do pai
            if (s.parentId === parent.parentId && s.order >= newOrder && s.id !== id) {
              return { ...s, order: s.order + 1 };
            }
            return s;
          }),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      toggleSectionCollapse: (id: string) => {
        set(state => ({
          sections: state.sections.map(s =>
            s.id === id ? { ...s, isCollapsed: !s.isCollapsed } : s
          ),
        }));
      },

      // ============================================================
      // BLOCOS (ALOCAÇÃO LEGADO)
      // ============================================================

      allocateBlock: (blockId: string, sectionId: string) => {
        const { placements } = get();
        
        // Verificar se já está alocado
        if (placements.some(p => p.blockId === blockId)) {
          // Remove da posição atual primeiro
          get().deallocateBlock(blockId);
        }
        
        // Calcular ordem
        const sectionPlacements = placements.filter(p => p.sectionId === sectionId);
        const order = sectionPlacements.length;
        
        const newPlacement: OutlineItemPlacement = {
          id: generateId('placement'),
          blockId,
          sectionId,
          order,
          createdAt: new Date().toISOString(),
        };
        
        set(state => ({
          placements: [...state.placements, newPlacement],
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      deallocateBlock: (blockId: string) => {
        set(state => ({
          placements: state.placements.filter(p => p.blockId !== blockId),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      moveBlock: (blockId: string, toSectionId: string, toIndex?: number) => {
        const { placements } = get();
        const placement = placements.find(p => p.blockId === blockId);
        if (!placement) return;
        
        // Calcular nova ordem
        const targetPlacements = placements
          .filter(p => p.sectionId === toSectionId && p.blockId !== blockId)
          .sort((a, b) => a.order - b.order);
        
        const newOrder = toIndex !== undefined ? toIndex : targetPlacements.length;
        
        set(state => ({
          placements: state.placements.map(p =>
            p.blockId === blockId
              ? { ...p, sectionId: toSectionId, order: newOrder }
              : p
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      moveBlockInSection: (blockId: string, direction: MoveDirection) => {
        const { placements } = get();
        const placement = placements.find(p => p.blockId === blockId);
        if (!placement) return;
        
        const sectionPlacements = placements
          .filter(p => p.sectionId === placement.sectionId)
          .sort((a, b) => a.order - b.order);
        
        const currentIndex = sectionPlacements.findIndex(p => p.blockId === blockId);
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        if (newIndex < 0 || newIndex >= sectionPlacements.length) return;
        
        const targetBlockId = sectionPlacements[newIndex].blockId;
        
        set(state => ({
          placements: state.placements.map(p => {
            if (p.blockId === blockId) return { ...p, order: newIndex };
            if (p.blockId === targetBlockId) return { ...p, order: currentIndex };
            return p;
          }),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      isBlockAllocated: (blockId: string) => {
        return get().placements.some(p => p.blockId === blockId);
      },

      getBlockSection: (blockId: string) => {
        const placement = get().placements.find(p => p.blockId === blockId);
        return placement?.sectionId || null;
      },

      getBlockPlacement: (blockId: string) => {
        return get().placements.find(p => p.blockId === blockId) || null;
      },

      // ============================================================
      // 🆕 v3.0: ITEMS (Enviados de Conexões)
      // ============================================================

      addItem: (item) => {
        const id = generateId('item');
        const { items } = get();
        
        // Calcular ordem no inbox (sectionId === null)
        const inboxItems = items.filter(i => i.sectionId === item.sectionId);
        const order = inboxItems.length;
        
        const newItem: OutlineItem = {
          ...item,
          id,
          order,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        set(state => ({
          items: [...state.items, newItem],
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
        
        return id;
      },

      addItems: (newItems) => {
        const { items } = get();
        const now = new Date().toISOString();
        
        // Calcular ordem inicial no inbox
        const inboxItems = items.filter(i => i.sectionId === null);
        let baseOrder = inboxItems.length;
        
        const itemsToAdd: OutlineItem[] = newItems.map((item, index) => ({
          ...item,
          id: generateId('item'),
          order: baseOrder + index,
          createdAt: now,
          updatedAt: now,
        }));
        
        set(state => ({
          items: [...state.items, ...itemsToAdd],
          lastModified: now,
          isDirty: true,
        }));
        
        return itemsToAdd.map(i => i.id);
      },

      removeItem: (itemId: string) => {
        set(state => ({
          items: state.items.filter(i => i.id !== itemId),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      updateItem: (itemId: string, updates: Partial<OutlineItem>) => {
        set(state => ({
          items: state.items.map(i =>
            i.id === itemId
              ? { ...i, ...updates, updatedAt: new Date().toISOString() }
              : i
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      moveItemToSection: (itemId: string, sectionId: string | null) => {
        const { items } = get();
        
        // Calcular nova ordem na seção destino
        const targetItems = items.filter(i => i.sectionId === sectionId && i.id !== itemId);
        const newOrder = targetItems.length;
        
        set(state => ({
          items: state.items.map(i =>
            i.id === itemId
              ? { ...i, sectionId, order: newOrder, updatedAt: new Date().toISOString() }
              : i
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      moveItemPosition: (itemId: string, direction: MoveDirection) => {
        const { items } = get();
        const item = items.find(i => i.id === itemId);
        if (!item) return;
        
        const sectionItems = items
          .filter(i => i.sectionId === item.sectionId)
          .sort((a, b) => a.order - b.order);
        
        const currentIndex = sectionItems.findIndex(i => i.id === itemId);
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        if (newIndex < 0 || newIndex >= sectionItems.length) return;
        
        const targetId = sectionItems[newIndex].id;
        
        set(state => ({
          items: state.items.map(i => {
            if (i.id === itemId) return { ...i, order: newIndex };
            if (i.id === targetId) return { ...i, order: currentIndex };
            return i;
          }),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      indentItem: (itemId: string) => {
        const { items } = get();
        const item = items.find(i => i.id === itemId);
        if (!item || item.level >= 3) return; // Máximo 3 níveis
        
        set(state => ({
          items: state.items.map(i =>
            i.id === itemId
              ? { ...i, level: i.level + 1, updatedAt: new Date().toISOString() }
              : i
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      outdentItem: (itemId: string) => {
        const { items } = get();
        const item = items.find(i => i.id === itemId);
        if (!item || item.level <= 0) return;
        
        set(state => ({
          items: state.items.map(i =>
            i.id === itemId
              ? { ...i, level: i.level - 1, updatedAt: new Date().toISOString() }
              : i
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      getInboxItems: () => {
        return get().items
          .filter(i => i.sectionId === null)
          .sort((a, b) => a.order - b.order);
      },

      getSectionItems: (sectionId: string) => {
        return get().items
          .filter(i => i.sectionId === sectionId)
          .sort((a, b) => a.order - b.order);
      },

      getItemCount: () => {
        const { items } = get();
        return {
          inbox: items.filter(i => i.sectionId === null).length,
          total: items.length,
        };
      },

      // ============================================================
      // 🆕 v3.0: SELEÇÃO EM CONEXÕES
      // ============================================================

      toggleContentSelection: (content: SelectedBlockContent) => {
        set(state => {
          const exists = state.selectedContents.some(
            c => c.blockId === content.blockId && c.type === content.type
          );
          
          if (exists) {
            return {
              selectedContents: state.selectedContents.filter(
                c => !(c.blockId === content.blockId && c.type === content.type)
              ),
            };
          } else {
            return {
              selectedContents: [...state.selectedContents, content],
            };
          }
        });
      },

      clearSelection: () => {
        set({ selectedContents: [] });
      },

      isContentSelected: (blockId: string, type: SelectableContentType) => {
        return get().selectedContents.some(
          c => c.blockId === blockId && c.type === type
        );
      },

      sendSelectionToOutline: () => {
        const { selectedContents, addItems, clearSelection, openPanel } = get();
        
        if (selectedContents.length === 0) return;
        
        // Converter seleção para OutlineItems
        const itemsToAdd: Omit<OutlineItem, 'id' | 'order' | 'createdAt' | 'updatedAt'>[] = 
          selectedContents.map(content => ({
            content: content.content,
            type: content.type,
            reference: content.reference,
            source: {
              blockId: content.blockId,
              highlightId: content.blockId,
              tag: content.tag,
            },
            sectionId: null, // Vai para inbox
            level: 0,
          }));
        
        // Adicionar itens
        addItems(itemsToAdd);
        
        // Limpar seleção
        clearSelection();
        
        // Abrir painel do outline
        openPanel();
      },

      getSelectionCount: () => {
        return get().selectedContents.length;
      },

      // ============================================================
      // NOTAS LIVRES
      // ============================================================

      addNote: (sectionId: string, text: string) => {
        const id = generateId('note');
        const { notes } = get();
        
        const sectionNotes = notes.filter(n => n.sectionId === sectionId);
        const order = sectionNotes.length;
        
        const newNote: OutlineNote = {
          id,
          sectionId,
          text,
          order,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        set(state => ({
          notes: [...state.notes, newNote],
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
        
        return id;
      },

      updateNote: (noteId: string, text: string) => {
        set(state => ({
          notes: state.notes.map(n =>
            n.id === noteId
              ? { ...n, text, updatedAt: new Date().toISOString() }
              : n
          ),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      deleteNote: (noteId: string) => {
        set(state => ({
          notes: state.notes.filter(n => n.id !== noteId),
          lastModified: new Date().toISOString(),
          isDirty: true,
        }));
      },

      // ============================================================
      // JANELA FLUTUANTE
      // ============================================================

      openPanel: () => {
        const { isPinned, panelState } = get();
        
        if (isPinned) {
          // Se pinned, abre na posição salva
          set(state => ({
            panelState: { ...state.panelState, isOpen: true },
          }));
        } else {
          // Se não pinned, centraliza na tela
          const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
          const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
          const { size } = panelState;
          
          const centerX = Math.max(50, (windowWidth - size.width) / 2);
          const centerY = Math.max(50, (windowHeight - size.height) / 2);
          
          set(state => ({
            panelState: { 
              ...state.panelState, 
              isOpen: true,
              position: { x: centerX, y: centerY }
            },
          }));
        }
      },

      closePanel: () => {
        set(state => ({
          panelState: { ...state.panelState, isOpen: false },
        }));
      },

      togglePanel: () => {
        const { panelState } = get();
        if (panelState.isOpen) {
          get().closePanel();
        } else {
          get().openPanel();
        }
      },

      togglePin: () => {
        const { isPinned, panelState } = get();
        const newPinned = !isPinned;
        set({ isPinned: newPinned });
        
        // Salvar estado de UI
        saveUIState({
          position: panelState.position,
          size: panelState.size,
          isPinned: newPinned,
        });
      },

      updatePanelPosition: (x: number, y: number) => {
        const { isPinned, panelState } = get();
        set(state => ({
          panelState: { ...state.panelState, position: { x, y } },
        }));
        
        // Salvar se pinned
        if (isPinned) {
          saveUIState({
            position: { x, y },
            size: panelState.size,
            isPinned,
          });
        }
      },

      updatePanelSize: (width: number, height: number) => {
        const { isPinned, panelState } = get();
        set(state => ({
          panelState: { ...state.panelState, size: { width, height } },
        }));
        
        // Salvar se pinned
        if (isPinned) {
          saveUIState({
            position: panelState.position,
            size: { width, height },
            isPinned,
          });
        }
      },

      setFocusedSection: (sectionId: string | null) => {
        set(state => ({
          panelState: { ...state.panelState, focusedSectionId: sectionId },
        }));
      },

      setFocusedBlock: (blockId: string | null) => {
        set(state => ({
          panelState: { ...state.panelState, focusedBlockId: blockId },
        }));
      },

      // ============================================================
      // PREFERÊNCIAS
      // ============================================================

      updateViewPreferences: (prefs: Partial<OutlineViewPreferences>) => {
        set(state => ({
          viewPreferences: { ...state.viewPreferences, ...prefs },
        }));
      },

      // ============================================================
      // PERSISTÊNCIA
      // ============================================================

      loadFromProject: (outline: ProjectOutline | null) => {
        if (!outline) {
          set({
            sections: [],
            placements: [],
            items: [],
            notes: [],
            lastModified: new Date().toISOString(),
            isDirty: false,
            isLoading: false,
          });
          return;
        }
        
        set({
          sections: outline.sections || [],
          placements: outline.placements || [],
          items: outline.items || [],
          notes: outline.notes || [],
          lastModified: outline.lastModified || new Date().toISOString(),
          isDirty: false,
          isLoading: false,
        });
      },

      getProjectOutline: (): ProjectOutline => {
        const { sections, placements, items, notes, lastModified } = get();
        return {
          sections,
          placements,
          items,
          notes,
          lastModified,
        };
      },

      markAsSaved: () => {
        set({ isDirty: false, isSaving: false });
      },

      // ============================================================
      // HELPERS
      // ============================================================

      getSectionHierarchy: (): SectionWithBlocks[] => {
        const { sections, placements, items, notes } = get();
        
        const buildTree = (parentId: string | null): SectionWithBlocks[] => {
          return sections
            .filter(s => s.parentId === parentId)
            .sort((a, b) => a.order - b.order)
            .map(section => ({
              ...section,
              blocks: placements
                .filter(p => p.sectionId === section.id)
                .sort((a, b) => a.order - b.order),
              items: items
                .filter(i => i.sectionId === section.id)
                .sort((a, b) => a.order - b.order),
              notes: notes
                .filter(n => n.sectionId === section.id)
                .sort((a, b) => a.order - b.order),
              children: buildTree(section.id),
            }));
        };
        
        return buildTree(null);
      },

      getSectionById: (id: string) => {
        return get().sections.find(s => s.id === id);
      },

      getBlocksInSection: (sectionId: string) => {
        return get().placements
          .filter(p => p.sectionId === sectionId)
          .sort((a, b) => a.order - b.order);
      },

      getNotesInSection: (sectionId: string) => {
        return get().notes
          .filter(n => n.sectionId === sectionId)
          .sort((a, b) => a.order - b.order);
      },

      getTotalBlocksAllocated: () => {
        return get().placements.length;
      },

      getSectionChildren: (parentId: string | null) => {
        return get().sections
          .filter(s => s.parentId === parentId)
          .sort((a, b) => a.order - b.order);
      },

      // Aliases para compatibilidade com ConexoesInspector
      removeBlockFromOutline: (blockId: string) => {
        get().deallocateBlock(blockId);
      },

      moveBlockPosition: (blockId: string, direction: MoveDirection) => {
        get().moveBlockInSection(blockId, direction);
      },

      moveBlockToSection: (blockId: string, toSectionId: string) => {
        get().moveBlock(blockId, toSectionId);
      },

      // ============================================================
      // RESET
      // ============================================================

      reset: () => {
        set({
          sections: [],
          placements: [],
          items: [],
          selectedContents: [],
          notes: [],
          lastModified: new Date().toISOString(),
          panelState: { ...DEFAULT_PANEL_STATE },
          viewPreferences: { ...DEFAULT_VIEW_PREFERENCES },
          isPinned: false,
          isLoading: false,
          isSaving: false,
          isDirty: false,
        });
      },
    }),
    { name: 'outline-store' }
  )
);
